import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@material-ui/core";
import { CheckCircle, RadioButtonUnchecked } from "@material-ui/icons";
import { ThreeDots } from "react-loader-spinner";

export default function SectionTables({
  tables,
  selectedTable,
  onSelectTable,
  setupkeyMerges,
  setupKeyMappings,
  isLoadingMappings,
  inputColumns,
  clientColor,
  classes,
}) {
  // Calculate status for each table
  const tableStatuses = useMemo(() => {
    if (!setupkeyMerges || !setupKeyMappings || !inputColumns) return {};

    const statuses = {};
    tables.forEach((table) => {
      // setupkeyMerges structure: { tableId: { "model01": [...keys], "tableId": [...keys] } }
      const tableData = setupkeyMerges[table.id];

      if (!tableData || typeof tableData !== "object") {
        statuses[table.id] = { defined: 0, total: 0, status: "inactive" };
        return;
      }

      // Collect ONLY setup keys with column_source='created' from ALL lists (model01, tax02, etc.)
      // Store both the key and its source table to build correct composite keys
      const allSetupKeys = [];
      Object.entries(tableData).forEach(([sourceTable, keysList]) => {
        if (Array.isArray(keysList)) {
          keysList.forEach((key) => {
            // Filter: Only include setup keys with column_source='created'
            const columnData = inputColumns?.[sourceTable]?.columns?.[key];
            if (columnData?.column_source === "created") {
              allSetupKeys.push({ key, sourceTable });
            }
          });
        }
      });

      const totalKeys = allSetupKeys.length;

      if (totalKeys === 0) {
        statuses[table.id] = { defined: 0, total: 0, status: "inactive" };
        return;
      }

      // Count how many keys have mappings defined
      let definedKeys = 0;
      allSetupKeys.forEach(({ key: setupKey, sourceTable }) => {
        // Use the SOURCE table (where the key belongs) for the composite key
        const compositeKey = `${sourceTable}__${setupKey}`;
        const columns = setupKeyMappings[compositeKey] || [];
        if (columns.length > 0) {
          definedKeys++;
        }
      });

      let status = "inactive";
      if (definedKeys === totalKeys) {
        status = "complete";
      } else if (definedKeys > 0) {
        status = "partial";
      }

      statuses[table.id] = { defined: definedKeys, total: totalKeys, status };
    });

    return statuses;
  }, [tables, setupkeyMerges, setupKeyMappings, inputColumns]);

  const getStatusColor = (status) => {
    switch (status) {
      case "complete":
        return "#4caf50"; // Green
      case "partial":
        return "#ff9800"; // Orange/Yellow
      default:
        return "#9e9e9e"; // Gray
    }
  };

  const getStatusIcon = (status) => {
    if (status === "complete") {
      return <CheckCircle style={{ fontSize: "1.25rem" }} />;
    }
    return <RadioButtonUnchecked style={{ fontSize: "1.25rem" }} />;
  };

  // Only show tables that actually have setup keys — a table with none has
  // nothing to configure here, so its card is hidden (e.g. Cadastro Regiões
  // once its key stopped being a setup key). Uses the same is_setup_key-driven
  // count as the status badge.
  const visibleTables = tables.filter(
    (table) => (tableStatuses[table.id]?.total || 0) > 0,
  );

  return (
    <Box className={classes.tablesColumn}>
      {visibleTables.map((table) => {
        const tableStatus = tableStatuses[table.id] || {
          defined: 0,
          total: 0,
          status: "inactive",
        };

        return (
          <Box
            key={table.id}
            className={
              selectedTable === table.id
                ? `${classes.tableButton} selected`
                : classes.tableButton
            }
            onClick={() => onSelectTable(table)}
          >
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography className={classes.tableName}>
                {table.description}
              </Typography>

              {isLoadingMappings ? (
                <Box
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "40px",
                  }}
                >
                  <ThreeDots
                    color={clientColor || "#1976d2"}
                    height={20}
                    width={20}
                    ariaLabel="loading"
                  />
                </Box>
              ) : (
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "2px",
                  }}
                >
                  <Box
                    style={{
                      color: getStatusColor(tableStatus.status),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {getStatusIcon(tableStatus.status)}
                  </Box>
                  <Typography
                    style={{
                      fontSize: "0.65rem",
                      fontWeight: "600",
                      color: getStatusColor(tableStatus.status),
                    }}
                  >
                    ({tableStatus.defined}/{tableStatus.total})
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

SectionTables.propTypes = {
  tables: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selectedTable: PropTypes.string,
  onSelectTable: PropTypes.func.isRequired,
  setupkeyMerges: PropTypes.object,
  setupKeyMappings: PropTypes.object,
  isLoadingMappings: PropTypes.bool,
  inputColumns: PropTypes.object,
  clientColor: PropTypes.string,
  classes: PropTypes.object.isRequired,
};
